import React from "react";
import { useField } from "formik";

import { IS_BROWSER } from "@config/consts";

import { Link } from "@components/Navigation/Link";

import { TestWrapper } from "@ui/Atoms";

import { useBreakpoint } from "@hooks/useBreakpoint";
import { useOnClickOutside } from "@hooks/useOnClickOutside";

import { Field } from "../Field";

import { SelectProps } from "./Select.types";
import * as Styled from "./Select.styled";

export const Select: React.FC<SelectProps> = props => {
  const [field, metaField, helpers] = useField<string>(props.name);
  const { mobile, largeMobile } = useBreakpoint();
  const [isActive, setIsActive] = React.useState(false);

  const $root = useOnClickOutside<HTMLFieldSetElement>(() => {
    setIsActive(false);
  });
  const selectedOption = props.options.find(
    option => option.value === field.value,
  );
  const longestLabel = props.options.reduce(
    (longest, option) =>
      option.label.length > longest.length ? option.label : longest,
    "",
  );

  const $list = React.useRef<HTMLUListElement>(null);

  const keyDownHandler = (
    event: React.KeyboardEvent<HTMLInputElement>,
  ): void => {
    if (event.key === "Enter") {
      setIsActive(false);
    }

    props.onKeyDown?.(event);
  };

  React.useEffect(() => {
    setIsActive(false);
  }, [mobile, largeMobile]);

  React.useEffect(() => {
    if (isActive) {
      props.onFocus?.(field.value);
    } else {
      props.onBlur?.(field.value);
    }
  }, [isActive]);

  React.useEffect(() => {
    if (!IS_BROWSER) return;

    const windowBlurHandler = () => {
      setIsActive(false);
    };

    window.addEventListener("blur", windowBlurHandler);

    return () => {
      window.removeEventListener("blur", windowBlurHandler);
    };
  }, []);

  React.useEffect(() => {
    if (!$list.current) return;

    const mutationCallback = (mutationsList: MutationRecord[]) => {
      if (!$list.current) return;

      for (const mutation of mutationsList) {
        if (mutation.type === "childList") {
          $list.current.scrollTop = 0;

          break;
        }
      }
    };

    const observer = new MutationObserver(mutationCallback);

    observer.observe($list.current, { childList: true });

    return () => {
      observer.disconnect();
    };
  }, [$list.current]);

  return (
    <Styled.Root ref={$root} $isActive={isActive}>
      {props.isNativeReady && !props.isSearchable && (
        <Styled.NativeField
          name={props.name}
          as="select"
          onChange={(event: React.ChangeEvent<HTMLSelectElement>) => {
            const newValue = event.currentTarget.value;

            helpers.setValue(newValue);
            props.onChange?.(newValue);
            props.onSelect?.(
              props.options.find(option => option.value === newValue)!,
            );
          }}
        >
          {props.options.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Styled.NativeField>
      )}

      {!props.isSearchable && (
        <TestWrapper testId={props.testId}>
          <Field as="button" type="button" onClick={() => setIsActive(c => !c)}>
            <Styled.Label>
              <Styled.LongestLabel aria-hidden="true">
                {longestLabel}
              </Styled.LongestLabel>
              <Styled.ActiveLabel>
                {selectedOption?.value.length
                  ? selectedOption.label
                  : props.placeholder}
              </Styled.ActiveLabel>
            </Styled.Label>
            <Styled.Icon $isActive={isActive} />
          </Field>
        </TestWrapper>
      )}

      {props.isSearchable && (
        <React.Fragment>
          <TestWrapper testId={props.testId}>
            <Field
              type="text"
              $hasError={metaField.error && metaField.touched}
              name={props.name}
              placeholder={props.placeholder}
              onFocus={() => setIsActive(true)}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                const newValue = event.currentTarget.value;

                helpers.setValue(newValue);
                props.onChange?.(newValue);
              }}
              onKeyDown={keyDownHandler}
            />
          </TestWrapper>
          {metaField.error && metaField.touched && (
            <Styled.ErrorMessage>{metaField.error}</Styled.ErrorMessage>
          )}
        </React.Fragment>
      )}

      <Styled.List
        ref={$list}
        $position={props.position || "bottom"}
        $isActive={props.options.length ? isActive : false}
      >
        {props.options.map(option => (
          <li key={option.value}>
            <TestWrapper testId={option.testId}>
              <Link
                as="button"
                type="button"
                onClick={() => {
                  setIsActive(false);
                  helpers.setValue(option.value);
                  props.onSelect?.(option);
                }}
                $style={{
                  default: "regular_16",
                  smallDesktop: "regular_18",
                }}
                style={{
                  width: "100%",
                }}
              >
                {option.label}
              </Link>
            </TestWrapper>
          </li>
        ))}
      </Styled.List>
    </Styled.Root>
  );
};
